import {
  isTvPlayerChoice,
  DEFAULT_TV_PLAYER,
  type LocalizedString,
  type PremiseConfig,
  type SchemeBinding,
  type SchemeContent,
  type SchemeKnobValues,
  type SchemeManifest,
  type TvPlayerChoice,
} from "@shared/types/foyer";
import { defaultSchemeForPremise } from "@shared/schemes/manifests";
import { defaultSchemeBinding } from "@shared/schemes/content";
import {
  HOME_ZONE,
  INFO_ZONE,
  buildScreenGroups,
  screenSummary,
  type ScreenRow,
} from "../template/screensModel";
import { translateKey, type Translate } from "../../i18n/translate";

/**
 * The model behind Start & navigation → Start view: one row per item on the
 * TV's start-screen menu, in the order the TV draws them, each with the three
 * things an operator scans for - what it is called, what is in it, and
 * whether a guest actually sees it.
 *
 * Built on `buildScreenGroups` (the same join of order knobs and screen toggles
 * every template editor uses), so placement, licensing and certification rules
 * stay in one place. What this adds is the TV's CONTENT gate: an item that is
 * switched on but has nothing in it is hidden by the TV (`visibleFunctions`),
 * and the table must say so rather than call it "Active".
 */

/** The TV row's id. TV is not a screen or a knob - it is the fixed first
 *  item of every template's menu. */
export const TV_ITEM = "tv";

/** Information's own page, under Start view (the other items open a drawer). */
/** Start view → Information's two pages: the operator's own sections, and the
 *  standard ones (Wi-Fi, Welcome message, Instagram, Weather). Information
 *  itself is a drawer on Start view (`?item=info`). */
export const INFO_OWN_PATH = "/frontlayer/home/info/sections";
export const INFO_STANDARD_PATH = "/frontlayer/home/info/standard";

/**
 * Information's STANDARD sections, in this order, under these names (product
 * decision 2026-09-30). The manifest labels differ per template (some are
 * Swedish), so the CMS names them itself.
 */
const STANDARD_INFO_SECTIONS = [
  { id: "wifi", name: "templates:menuItems.standard.wifi" },
  { id: "welcome", name: "templates:menuItems.standard.welcome" },
  { id: "instagram", name: "templates:menuItems.standard.instagram" },
  { id: "weather", name: "templates:menuItems.standard.weather" },
] as const;

export function standardInfoSections(
  t: Translate
): { id: string; name: string }[] {
  return STANDARD_INFO_SECTIONS.map((s) => ({ id: s.id, name: t(s.name) }));
}

/** "Active" = the TV shows it. "Hidden" = switched on, but the TV hides it
 *  because it has nothing to show. */
export type MenuStatus = "active" | "off" | "hidden";

interface ItemMeta {
  name: string;
  description: string;
  /** False when the drawer holds nothing but the item's switch. */
  manageable: boolean;
}

const MANAGEABLE: Record<string, boolean> = {
  [TV_ITEM]: true,
  apps: true,
  info: true,
  radio: true,
  custom: true,
  vod: true,
  guide: true,
  cast: false,
  offers: false,
};

export interface MenuItem {
  /** Screen id (`apps`, `info` ...), or {@link TV_ITEM}. What `?item=` carries. */
  id: string;
  name: string;
  description: string;
  /** TV: always first, always shown, not orderable. */
  fixed: boolean;
  /** Drag-reorderable within the start screen's order knob. */
  orderable: boolean;
  /** The manifest row behind it. Absent only for TV. */
  row?: ScreenRow;
  on: boolean;
  /** The Title column. */
  title: string;
  /** The Content column. */
  content: string;
  manageable: boolean;
  status: MenuStatus;
}

/**
 * Counts the CMS cannot read from the binding alone. `undefined` = not known
 * (still loading, or the source failed) - never read as "empty", so an
 * unanswered request cannot mark an item hidden.
 */
export interface MenuCounts {
  /** Enabled apps after the global-catalogue merge (what the TV lists). */
  apps?: number;
  /** Stations after the global-catalogue merge. */
  stations?: number;
  /** Titles the preview room may watch, summed across its rails. */
  vodTitles?: number;
}

export interface MenuInput {
  manifest: SchemeManifest;
  knobs: SchemeKnobValues | undefined;
  content: SchemeContent;
  counts: MenuCounts;
  tvPlayer?: TvPlayerChoice;
  /** The premise has coordinates - Weather needs them to show at all. */
  hasCoordinates: boolean;
  hasPromotions: boolean;
  appsCertified: boolean;
  certificationNote?: string;
  /** Does the premise hold Streaming TV? Fixes "Films & series" on. */
  hasStreamingTv?: boolean;
}

/** The screen a row stands for. Rows built from an order knob are keyed by the
 *  screen id already; the rest are keyed by their toggle knob. */
export function screenIdOf(manifest: SchemeManifest, row: ScreenRow): string {
  const knob = manifest.knobs.find((k) => k.id === row.key);
  if (knob?.type === "toggle" && knob.controls?.kind === "screen") {
    return knob.controls.id;
  }
  return row.key;
}

/** Does this row stand for a screen (a menu item), rather than a banner such
 *  as the Promotion slot? */
function isScreenRow(manifest: SchemeManifest, row: ScreenRow): boolean {
  return row.orderable || screenIdOf(manifest, row) !== row.key;
}

/**
 * The two places a screen can sit: the start screen's menu, and the list inside
 * Information. Only screens - the Promotion banner is switched under
 * Promotions → Home screen slot, next to what it shows.
 */
export function menuZones(
  input: Pick<
    MenuInput,
    | "manifest"
    | "knobs"
    | "hasPromotions"
    | "appsCertified"
    | "certificationNote"
    | "hasStreamingTv"
  >,
  t: Translate
): { home: ScreenRow[]; info: ScreenRow[]; homeOrderKnobId?: string } {
  const groups = buildScreenGroups(t, input.manifest, input.knobs, {
    hasPromotions: input.hasPromotions,
    appsCertified: input.appsCertified,
    certificationNote: input.certificationNote,
    hasStreamingTv: input.hasStreamingTv,
  });
  const home = groups.find((g) => g.title === HOME_ZONE);
  const info = groups.find((g) => g.title === INFO_ZONE);
  const screens = (rows: ScreenRow[] | undefined) =>
    (rows ?? []).filter((r) => isScreenRow(input.manifest, r));
  return {
    home: screens(home?.rows),
    info: screens(info?.rows),
    homeOrderKnobId: home?.orderKnobId,
  };
}

/** Any language of a localised string has words in it. */
function hasText(v: LocalizedString | undefined): boolean {
  if (!v) return false;
  return typeof v === "string"
    ? v.trim() !== ""
    : Object.values(v).some((x) => x.trim() !== "");
}

/** Custom items worth drawing - the TV's `playableItems` rule (an item with
 *  nothing on it would paint a black page). */
function playableCustomItems(content: SchemeContent): number {
  return (content.custom?.items ?? []).filter(
    (it) => it.imageUrl || hasText(it.text?.body) || it.qr?.url.trim()
  ).length;
}

/** The Information page's entries: its own sections plus every switched-on
 *  screen filed under it (Weather only with coordinates) - the TV's count. */
export function informationCount(
  manifest: SchemeManifest,
  infoRows: ScreenRow[],
  content: SchemeContent,
  hasCoordinates: boolean
): number {
  const subs = infoRows.filter(
    (r) => r.on && (screenIdOf(manifest, r) !== "weather" || hasCoordinates)
  ).length;
  return content.infoSections.length + subs;
}

/**
 * How many things the item holds, as the TV counts them to decide whether to
 * show it. `undefined` = the TV applies no content gate here, or the CMS
 * cannot know.
 */
function contentCount(
  id: string,
  input: MenuInput,
  infoRows: ScreenRow[]
): number | undefined {
  switch (id) {
    case "apps":
      return input.counts.apps;
    case "radio":
      return input.counts.stations;
    case "info":
      return informationCount(
        input.manifest,
        infoRows,
        input.content,
        input.hasCoordinates
      );
    case "custom":
      return playableCustomItems(input.content);
    case "vod":
      return input.counts.vodTitles;
    default:
      return undefined;
  }
}

function contentLabel(
  t: Translate,
  id: string,
  count: number | undefined,
  input: MenuInput,
  row: ScreenRow | undefined
): string {
  switch (id) {
    case TV_ITEM:
      return input.tvPlayer === "foyer"
        ? t("templates:tvPlayerSetting.players.foyer")
        : t("templates:tvPlayerSetting.players.norigin");
    case "apps":
      return count === undefined
        ? t("common:states.loading")
        : t("glossary:appCount", { count });
    case "radio":
      return count === undefined
        ? t("common:states.loading")
        : t("templates:menuItems.stationCount", { count });
    case "info":
      return t("templates:menuItems.sectionCount", { count: count ?? 0 });
    case "custom":
      return t("templates:menuItems.itemCount", { count: count ?? 0 });
    case "vod":
      return count === undefined
        ? t("templates:menuItems.titlesUnavailable")
        : t("glossary:titleCount", { count });
    case "guide":
      return t("templates:menuItems.showingLineup");
    case "cast":
      return "–";
    default:
      return screenSummary(t, row?.sub, input.content) ?? "–";
  }
}

function statusOf(
  fixed: boolean,
  on: boolean,
  count: number | undefined
): MenuStatus {
  if (fixed) return "active";
  if (!on) return "off";
  return count === 0 ? "hidden" : "active";
}

/** A row the table knows by name, else the manifest's own word for it. */
function metaFor(
  t: Translate,
  id: string,
  row: ScreenRow | undefined
): ItemMeta {
  const manageable = MANAGEABLE[id];
  if (manageable === undefined) {
    return {
      name: row?.label ?? id,
      description: row?.help ?? "",
      manageable: !!row?.sub,
    };
  }
  return {
    name: translateKey(t, `templates:menuItems.items.${id}.name`),
    description: translateKey(t, `templates:menuItems.items.${id}.description`),
    manageable,
  };
}

/**
 * TV first, fixed; then the start screen's items in the order the TV draws
 * them.
 */
export function buildMenuItems(
  input: MenuInput,
  t: Translate
): {
  items: MenuItem[];
  infoRows: ScreenRow[];
  homeOrderKnobId?: string;
} {
  const zones = menuZones(input, t);
  const tvPlayer = isTvPlayerChoice(input.tvPlayer)
    ? input.tvPlayer
    : DEFAULT_TV_PLAYER;
  const tvMeta = metaFor(t, TV_ITEM, undefined);
  const defaultTitle = t("templates:menuItems.defaultTitle");
  const tv: MenuItem = {
    id: TV_ITEM,
    name: tvMeta.name,
    description: tvMeta.description,
    fixed: true,
    orderable: false,
    on: true,
    title: defaultTitle,
    content: contentLabel(
      t,
      TV_ITEM,
      undefined,
      { ...input, tvPlayer },
      undefined
    ),
    manageable: true,
    status: "active",
  };
  const rest = zones.home.map((row): MenuItem => {
    const id = screenIdOf(input.manifest, row);
    const meta = metaFor(t, id, row);
    const count = contentCount(id, input, zones.info);
    const on = row.fixedNote ? true : row.on;
    return {
      id,
      name: meta.name,
      description: meta.description,
      fixed: false,
      orderable: row.orderable && !!row.orderKnobId,
      row,
      on,
      title: row.title?.raw.trim() || defaultTitle,
      // Screen sharing has no content at all ("–", as drawn); Campaign's lives
      // under Promotions, so here it is "No manageable content".
      content:
        meta.manageable || id === "cast"
          ? contentLabel(t, id, count, input, row)
          : t("templates:menuItems.noManageableContent"),
      manageable: meta.manageable,
      status: statusOf(false, on, count),
    };
  });
  return {
    items: [tv, ...rest],
    infoRows: zones.info,
    homeOrderKnobId: zones.homeOrderKnobId,
  };
}

/**
 * Put `subset` back into `full` in its new order, leaving every other id where
 * it was. The table shows only the start screen's part of an order knob, so a
 * drag must not disturb ids it does not show.
 */
export function reorderSubset(full: string[], subset: string[]): string[] {
  const members = new Set(subset);
  const queue = subset.slice();
  return full.map((id) => (members.has(id) ? (queue.shift() ?? id) : id));
}

/**
 * The binding the page edits: the premise's own, or - when it never picked a
 * template - the one the TV renders anyway (the segment default, Nord for a
 * hotel), seeded with the same demo content the TV falls back to. Nothing is
 * written until the operator changes something.
 */
export function effectiveBinding(premise: PremiseConfig): SchemeBinding {
  if (premise.scheme) return premise.scheme;
  return defaultSchemeBinding(defaultSchemeForPremise(premise), {
    hotelName: premise.premiseName || "The Hotel",
    city: premise.address?.city,
  });
}
